import { useEffect, useState } from 'react'
import { HeadProvider, Title } from 'react-head'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router'

import LogoImg from '@/assets/images/logo.svg?url'
import { parseJwt } from '@/common/utils/parseJwt'
import { useActivateUser } from '../../hooks/useActivateUser'
import { ActivatingMessage } from './components/ActivatingMessage'
import { ActivationForm } from './components/ActivationForm'
import { ErrorMessage } from './components/ErrorMessage'
import { InvalidTokenMessage } from './components/InvalidTokenMessage'
import { SuccessMessage } from './components/SuccessMessage'

export default function ActivateUser() {
  const [newPasswordToken, setNewPasswordToken] = useState<string>('')
  const [invalidToken, setInvalidToken] = useState(false)
  const { t } = useTranslation('login')
  const {
    handleActivateUser: activateUser,
    isPendingActivateUser: isPending,
    isSuccessActivateUser: isSuccess,
    isErrorActivateUser: isError,
  } = useActivateUser()
  const [searchParams] = useSearchParams()

  const token = searchParams.get('verify-token')

  const handleActivateUserClick = async () => {
    if (!token) return

    const response = await activateUser(token)
    setNewPasswordToken(response.data.newPasswordToken)
  }

  useEffect(() => {
    if (!token) {
      setInvalidToken(true)
      return
    }

    try {
      parseJwt(token)
    } catch {
      setInvalidToken(true)
    }
  }, [token])

  const getActivationState = () => {
    if (invalidToken) return 'invalidToken'
    if (isPending) return 'pending'
    if (isSuccess) return 'success'
    if (isError) return 'error'
    return 'form'
  }

  const ACTIVATION_COMPONENTS = {
    invalidToken: <InvalidTokenMessage />,
    pending: <ActivatingMessage />,
    success: <SuccessMessage newPasswordToken={newPasswordToken} />,
    error: <ErrorMessage />,
    form: (
      <ActivationForm
        onActivateUser={handleActivateUserClick}
        isPending={isPending}
      />
    ),
  }

  return (
    <div className="flex flex-col gap-32 h-full w-full">
      <HeadProvider>
        <Title>{t('activate_user.browser.title')}</Title>
      </HeadProvider>

      <img
        src={LogoImg}
        className="w-1/4 mx-auto"
        alt={t('sign_in.logo.alt')}
      />

      <div className="max-w-md mx-auto w-full">
        {ACTIVATION_COMPONENTS[getActivationState()]}
      </div>

      <div className="flex flex-col gap-4">
        <p className="text-base-400 font-light text-xs text-center">
          {t('sign_in.footer.copyright.text')}
        </p>
      </div>
    </div>
  )
}
